import React, { useMemo, useState, useCallback } from "react";
import { useFlow } from "@/contexts/FlowContext";
import { BuildSteps, DeploySteps, Flows } from "@/types/flows";
import { RelabelConfirmationModal } from "@/components/flow/shared/RelabelConfirmationModal";
import { usePredictionsForFileAndFrameSegment } from "@/store";
import { useAuth } from "@/contexts/AuthContext";
import { useCurrentWorkspace } from "@/store";
import { useDeployContext } from "@/contexts/DeployContext";
import { useComputedValues } from "@/contexts/ComputedValuesContext";
import { FeedbackForm } from "@/components/FeedbackForm";
import { useFlowState } from "@/hooks/useFlowState";
import { useNavigate } from "react-router-dom";

interface StepProps {
    title: string;
    description?: string;
    isActive?: boolean;
    isCompleted?: boolean;
    isDisabled?: boolean;
    isGenerating?: boolean;
    onClick?: () => void;
}

const Step: React.FC<StepProps> = ({
    title,
    isActive = false,
    isCompleted = false,
    isDisabled = false,
    isGenerating = false,
    onClick
}) => {
    const handleClick = () => {
        if (!isDisabled && onClick) {
            onClick();
        }
    };

    return (
        <div
            className={`flex items-center gap-2 transition-all duration-200 ${isDisabled ? "cursor-not-allowed opacity-50" : "cursor-default"}`}
            // onClick={handleClick}
        >
            {isGenerating ? (
                <i className="fas fa-spinner fa-spin text-sm text-brand"></i>
            ) : isActive ? (
                <i className="fas fa-circle-dot text-sm text-brand"></i>
            ) : isCompleted ? (
                <i className="fas fa-check text-sm text-tertiary"></i>
            ) : (
                <i className="far fa-circle text-sm text-tertiary"></i>
            )}
            <div
                className={`text-sm ${isActive ? "font-semibold text-brand" : isCompleted ? "font-normal text-tertiary" : "font-normal text-tertiary"}`}
            >
                {title}
            </div>
        </div>
    );
};

export const FlowStepper = () => {
    const { task, flowLocked } = useFlow();
    const { setStep, flow, step } = useFlowState();
    const { currentPromptingData, currentInferenceSampledData } = useComputedValues();
    const predictionsForFileAndFrameSegment = usePredictionsForFileAndFrameSegment();
    const [showRelabelConfirmation, setShowRelabelConfirmation] = useState(false);

    // Deploy functionality hooks
    const { createRapidWorkflowDeploy, creatingRapidWorkflow } = useDeployContext();
    const { user } = useAuth();
    const currentWorkspace = useCurrentWorkspace();
    const navigate = useNavigate();

    // Handle deploy/integrate functionality
    const handleCreateRapidWorkflow = useCallback(async () => {
        if (!user) {
            setStep({ flow: Flows.BUILD, step: BuildSteps.PREVIEW });
            return;
        }

        try {
            const workflow = await createRapidWorkflowDeploy({
                task,
                workspaceId: currentWorkspace?.id,
                prompts: currentPromptingData,
                inferenceData: currentInferenceSampledData
                // isSync: true // -- Set this to true if you want to run the whole first setup synchronously to debug
            });
            setStep({ flow: Flows.DEPLOY, step: DeploySteps.DEPLOY, workflowUrl: workflow.url });
        } catch (err) {
            console.error("FlowStepper handleCreateRapidWorkflow ", err);
            alert("There was an error creating your workflow. Please try again.");
            navigate("/");
        }
    }, [
        user,
        task,
        currentWorkspace,
        setStep,
        createRapidWorkflowDeploy,
        currentPromptingData,
        currentInferenceSampledData,
        navigate
    ]);

    const { steps, currentStep } = useMemo(() => {
        if (!flow) {
            return {
                steps: [{ title: "Loading...", description: "Loading steps" }],
                currentStep: 0
            };
        }

        if (flow === Flows.SETUP) {
            const setupSteps = [{ title: "Upload", description: "Upload your data" }];

            return { steps: setupSteps, currentStep: 0 };
        }

        // Both build and deploy flows show the same three steps
        const flowSteps = [
            { title: "Build", description: "Build your model" },
            { title: "Review", description: "Preview your model" },
            { title: "Use", description: "Integrate your model" }
        ];

        if (flow === Flows.BUILD) {
            const stepIndexMap = {
                [BuildSteps.PROMPT]: 0,
                [BuildSteps.REVIEW]: 1,
                [BuildSteps.PREVIEW]: 2
            };

            return {
                steps: flowSteps,
                currentStep: step
                    ? stepIndexMap[step as Exclude<BuildSteps, typeof BuildSteps.UPLOAD>]
                    : 0
            };
        } else if (flow === Flows.DEPLOY) {
            // For deploy flow, we're always on the "Use" step (index 2)
            return { steps: flowSteps, currentStep: 2 };
        } else if (flow === Flows.SHARE) {
            return {
                steps: [{ title: "Run the Model", description: "Run the model" }],
                currentStep: 0
            };
        }

        // This should never be reached, but just in case
        return { steps: [{ title: "Unknown", description: "Unknown step" }], currentStep: 0 };
    }, [flow, step]);

    // Check if model has been built using store predictions
    const hasModelBeenBuilt = useMemo(() => {
        if (flow !== Flows.BUILD) return false;
        return (
            Object.keys(predictionsForFileAndFrameSegment).length > 0 &&
            Object.values(predictionsForFileAndFrameSegment).some(
                (predictionsForFile) => Object.keys(predictionsForFile).length > 0
            )
        );
    }, [flow, predictionsForFileAndFrameSegment]);

    const handleStepClick = useCallback(
        (stepIndex: number) => {
            if (flow === Flows.BUILD) {
                if (flowLocked) {
                    return;
                }

                const canNavigateToPreviewUse =
                    hasModelBeenBuilt || step === BuildSteps.REVIEW || step === BuildSteps.PREVIEW;

                if ((stepIndex === 1 || stepIndex === 2) && !canNavigateToPreviewUse) {
                    return;
                }

                if (stepIndex === 0) {
                    if (hasModelBeenBuilt) {
                        setShowRelabelConfirmation(true);
                    } else {
                        setStep({ flow: Flows.BUILD, step: BuildSteps.PROMPT });
                    }
                } else if (stepIndex === 1) {
                    setStep({ flow: Flows.BUILD, step: BuildSteps.REVIEW });
                } else if (stepIndex === 2) {
                    // Integrate step - trigger the same functionality as "Deploy Your Model" button
                    handleCreateRapidWorkflow();
                }
            } else if (flow === Flows.DEPLOY) {
                // For deploy flow, clicking on steps should navigate back to build flow
                if (stepIndex === 0) {
                    // Build step - go to prompt
                    setStep({ flow: Flows.BUILD, step: BuildSteps.PROMPT });
                } else if (stepIndex === 1) {
                    // Preview step - go to review
                    setStep({ flow: Flows.BUILD, step: BuildSteps.REVIEW });
                } else if (stepIndex === 2) {
                    // Use step - stay in deploy flow (current behavior)
                    // This step is already active, so no action needed
                }
            }
        },
        [flow, flowLocked, handleCreateRapidWorkflow, hasModelBeenBuilt, setStep, step]
    );

    // Don't render if no steps
    if (!steps || steps.length === 0) {
        return null;
    }

    return (
        <>
            <div className="flex h-12 items-center justify-between gap-4 border-b bg-white px-6">
                <div className="flex items-center gap-3">
                    {steps.map((stepItem, index) => {
                        const isActive = index === currentStep;
                        const isCompleted = index < currentStep;

                        let isDisabled = false;
                        if (flow === Flows.BUILD) {
                            if (index === 0) {
                                isDisabled = flowLocked;
                            } else if (index === 1) {
                                const canNavigateToPreviewUse =
                                    hasModelBeenBuilt ||
                                    step === BuildSteps.REVIEW ||
                                    step === BuildSteps.PREVIEW;
                                isDisabled = !canNavigateToPreviewUse;
                            } else if (index === 2) {
                                // Integrate step - disable when generating model or creating workflow
                                const canNavigateToPreviewUse =
                                    hasModelBeenBuilt ||
                                    step === BuildSteps.REVIEW ||
                                    step === BuildSteps.PREVIEW;
                                isDisabled = !canNavigateToPreviewUse || creatingRapidWorkflow;
                            }
                        } else if (flow === Flows.DEPLOY) {
                            // For deploy flow, all steps should be clickable to navigate back
                            // Only disable if creating workflow
                            if (index === 2) {
                                isDisabled = creatingRapidWorkflow;
                            }
                        }

                        return (
                            <React.Fragment key={index}>
                                <Step
                                    title={stepItem.title}
                                    description={stepItem.description}
                                    isActive={isActive}
                                    isCompleted={isCompleted}
                                    isDisabled={isDisabled}
                                    isGenerating={
                                        (index === 0 && flowLocked) ||
                                        (index === 2 && creatingRapidWorkflow)
                                    }
                                    onClick={() => handleStepClick(index)}
                                />
                                {index < steps.length - 1 && (
                                    <div className="h-px w-10 bg-gray-300"></div>
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
                <FeedbackForm />
            </div>

            <RelabelConfirmationModal
                isOpen={showRelabelConfirmation}
                onClose={() => setShowRelabelConfirmation(false)}
                onConfirm={() => setShowRelabelConfirmation(false)}
                onCancel={() => {
                    setShowRelabelConfirmation(false);
                    setStep({ flow: Flows.BUILD, step: BuildSteps.PROMPT });
                }}
            />
        </>
    );
};

export default FlowStepper;
